/**
 * image-target-generator.ts
 *
 * Client-side image target descriptor generation for 8th Wall.
 * Generates image target descriptors from artwork metadata
 * by transforming source images to the expected target dimensions.
 */

import type { CreatorPayload } from "../creator/types";
import {
  CreatorArtworkCollectionType,
  CreatorArtworkImageTargetType,
  CreatorArtworkWorldTargetType,
  CreatorFileImageTargetType,
  CreatorFileImageType,
} from "../creator/types";

// =============================================================================
// TYPES
// =============================================================================

export interface ImageTargetProperties {
  top: number;
  left: number;
  width: number;
  height: number;
  isRotated: boolean;
  originalWidth: number;
  originalHeight: number;
}

export interface ImageTargetDescriptor {
  name: string;
  type: 'PLANAR';
  imagePath: string;
  properties: ImageTargetProperties;
  loadAutomatically: boolean;
}

export interface TargetDimensions {
  sourceWidth: number;
  sourceHeight: number;
  targetWidth: number;
  targetHeight: number;
  descriptorWidth: number;
  descriptorHeight: number;
}

export interface ArtworkImageSource {
  path: string;
  width?: number;
  height?: number;
}

export interface ArtworkTargetInput {
  id: string;
  targetImage?: ArtworkImageSource | null;
}

export interface ImageTargetGeneratorOptions {
  /** Default source width when not provided (default: 1024) */
  defaultSourceWidth?: number;
  /** Target aspect ratio for portrait images (default: 3/4) */
  portraitAspectRatio?: number;
  /** JPEG quality for generated images (default: 0.92) */
  jpegQuality?: number;
  /** Background color for letterboxing (default: '#FFFFFF') */
  backgroundColor?: string;
  /** Whether to load targets automatically (default: true) */
  loadAutomatically?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_SOURCE_WIDTH = 1024;
const DEFAULT_PORTRAIT_ASPECT_RATIO = 3 / 4;
const DEFAULT_JPEG_QUALITY = 0.92;
const DEFAULT_BACKGROUND_COLOR = '#FFFFFF';
const MAX_TARGET_HEIGHT = 2048;

// =============================================================================
// DIMENSION CALCULATIONS (Pure functions, no side effects)
// =============================================================================

const toPositiveInt = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  return Math.round(value);
};

/**
 * Calculate target dimensions from source image dimensions.
 * Ensures consistent sizing for 8th Wall image target tracking.
 */
export const calculateTargetDimensions = (
  sourceWidth?: number | null,
  sourceHeight?: number | null,
  options: Pick<ImageTargetGeneratorOptions, 'defaultSourceWidth' | 'portraitAspectRatio'> = {}
): TargetDimensions => {
  const defaultWidth = options.defaultSourceWidth ?? DEFAULT_SOURCE_WIDTH;
  const aspectRatio = options.portraitAspectRatio ?? DEFAULT_PORTRAIT_ASPECT_RATIO;

  const width = toPositiveInt(sourceWidth) ?? defaultWidth;
  const height = toPositiveInt(sourceHeight) ?? width;

  // Start with source width, calculate height from aspect ratio
  let targetWidth = width;
  let targetHeight = Math.round(targetWidth / aspectRatio);

  // If source image is taller than the canvas, expand to fit at original size
  if (height > targetHeight) {
    targetWidth = Math.ceil(height * aspectRatio);
    targetHeight = Math.round(targetWidth / aspectRatio);
  }

  // Cap at max height
  if (targetHeight > MAX_TARGET_HEIGHT) {
    targetHeight = MAX_TARGET_HEIGHT;
    targetWidth = Math.round(targetHeight * aspectRatio);
  }

  return {
    sourceWidth: width,
    sourceHeight: height,
    targetWidth,
    targetHeight,
    descriptorWidth: targetWidth,
    descriptorHeight: targetHeight % 2 === 1 ? targetHeight - 1 : targetHeight,
  };
};

/**
 * Build properties object for an image target descriptor.
 */
const buildTargetProperties = (dimensions: TargetDimensions): ImageTargetProperties => ({
  top: 0,
  left: 0,
  width: dimensions.descriptorWidth - 1,
  height: dimensions.descriptorHeight - 1,
  isRotated: false,
  originalWidth: dimensions.targetWidth,
  originalHeight: dimensions.targetHeight,
});

// =============================================================================
// IMAGE GENERATION (Browser-only, uses Canvas API)
// =============================================================================

/**
 * Load an image from a URL and return it as an HTMLImageElement.
 * Handles CORS by setting crossOrigin attribute.
 */
const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
};

/**
 * Generate a resized target image using Canvas API.
 * Returns a Blob URL that can be used as imagePath in the descriptor.
 */
const generateTargetImage = async (
  sourceUrl: string,
  dimensions: TargetDimensions,
  options: Pick<ImageTargetGeneratorOptions, 'jpegQuality' | 'backgroundColor'> = {}
): Promise<string> => {
  const quality = options.jpegQuality ?? DEFAULT_JPEG_QUALITY;
  const bgColor = options.backgroundColor ?? DEFAULT_BACKGROUND_COLOR;

  const img = await loadImage(sourceUrl);

  const canvas = document.createElement('canvas');
  canvas.width = dimensions.targetWidth;
  canvas.height = dimensions.targetHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas 2d context');
  }

  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const scale = Math.min(
    1,
    dimensions.targetWidth / img.width,
    dimensions.targetHeight / img.height
  );
  const scaledWidth = img.width * scale;
  const scaledHeight = img.height * scale;
  const offsetX = (dimensions.targetWidth - scaledWidth) / 2;
  const offsetY = (dimensions.targetHeight - scaledHeight) / 2;

  ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to generate image blob'));
          return;
        }
        resolve(URL.createObjectURL(blob));
      },
      'image/jpeg',
      quality
    );
  });
};

// =============================================================================
// DESCRIPTOR BUILDERS
// =============================================================================

/**
 * Build an image target descriptor without generating the image.
 * Use this when you have a pre-existing image URL (e.g., from an API endpoint).
 */
const buildDescriptor = (
  name: string,
  imagePath: string,
  dimensions: TargetDimensions,
  loadAutomatically = true
): ImageTargetDescriptor => ({
  name,
  type: 'PLANAR',
  imagePath,
  properties: buildTargetProperties(dimensions),
  loadAutomatically,
});

/**
 * Build an image target descriptor with client-side image generation.
 * Fetches the source image, resizes it, and creates a blob URL.
 */
const buildDescriptorWithImage = async (
  name: string,
  sourceUrl: string,
  dimensions: TargetDimensions,
  options: ImageTargetGeneratorOptions = {}
): Promise<ImageTargetDescriptor> => {
  const imagePath = await generateTargetImage(sourceUrl, dimensions, options);
  return buildDescriptor(name, imagePath, dimensions, options.loadAutomatically ?? true);
};

// =============================================================================
// HIGH-LEVEL API
// =============================================================================

/**
 * Generate image target descriptors from an array of artwork inputs.
 * Generates target images client-side using Canvas API.
 *
 * @param artworks - Array of artwork objects with id and artwork1024 fields
 * @param options - Generation options
 * @returns Promise resolving to array of image target descriptors
 *
 * @example
 * ```ts
 * const artworks = [
 *   {
 *     id: 'abc123',
 *     targetImage: { path: 'https://cdn.example.com/image.jpg', width: 1024, height: 768 }
 *   }
 * ];
 * const targets = await generateImageTargets(artworks);
 * ```
 */
export const generateImageTargets = async (
  artworks: ArtworkTargetInput[],
  options: ImageTargetGeneratorOptions = {}
): Promise<ImageTargetDescriptor[]> => {
  const results: ImageTargetDescriptor[] = [];

  for (const artwork of artworks) {
    if (!artwork.targetImage?.path) {
      continue;
    }

    const dimensions = calculateTargetDimensions(
      artwork.targetImage.width,
      artwork.targetImage.height,
      options
    );

    try {
      const descriptor = await buildDescriptorWithImage(
        artwork.id,
        artwork.targetImage.path,
        dimensions,
        options
      );
      results.push(descriptor);
    } catch (error) {
      console.warn(`Failed to generate target for ${artwork.id}:`, error);
    }
  }

  return results;
};

/**
 * Extract artwork target inputs from a Creator payload.
 * Handles both single artworks and collections.
 */
export const extractArtworkInputsFromPayload = (
  payload: CreatorPayload | null
): ArtworkTargetInput[] => {
  if (!payload) return [];

  const findFile = (
    files: unknown[] | undefined,
    type: string
  ): ArtworkImageSource | null => {
    if (!Array.isArray(files)) return null;
    const file = files.find(
      (f): f is { type: string; path?: string; width?: number; height?: number } =>
        typeof f === 'object' &&
        f !== null &&
        'type' in f &&
        (f as { type: string }).type?.toLowerCase() === type.toLowerCase()
    );
    if (!file?.path) return null;
    return { path: file.path, width: file.width, height: file.height };
  };

  const isWorldTargetArtwork = (art: { type?: string }): boolean =>
    art.type === CreatorArtworkWorldTargetType;

  const findTargetImageFile = (files: unknown[] | undefined): ArtworkImageSource | null => {
    return (
      findFile(files, CreatorFileImageTargetType) ||
      findFile(files, CreatorFileImageType)
    );
  };

  if (payload.type === CreatorArtworkCollectionType) {
    return payload.artworks
      .filter((artwork) => artwork.type !== CreatorArtworkWorldTargetType)
      .map((artwork) => ({
        id: artwork.id,
        targetImage: findTargetImageFile(artwork.files as unknown[] | undefined),
      }));
  }

  if (payload.type === CreatorArtworkImageTargetType && !isWorldTargetArtwork(payload)) {
    return [
      {
        id: payload.id,
        targetImage: findTargetImageFile(payload.files as unknown[] | undefined),
      },
    ];
  }

  return [];
};

